import { useState } from "react";
import { ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import {
  twoFactorEnableRequest,
  twoFactorEnableConfirm,
  twoFactorDisable,
} from "@/lib/api";
import { toast } from "sonner";

export default function TwoFactorSettings() {
  const { user, setUser } = useAuth();
  const enabled = !!user?.two_factor_enabled;
  const [step, setStep] = useState("idle"); // idle | confirm | disable
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const startEnable = async () => {
    setBusy(true);
    try {
      const { challenge_id } = await twoFactorEnableRequest();
      setChallengeId(challenge_id);
      setStep("confirm");
      toast.info("Wir haben Ihnen einen Bestätigungscode per E-Mail gesendet.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Code konnte nicht gesendet werden.");
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await twoFactorEnableConfirm(challengeId, code.trim());
      setUser(u);
      setStep("idle");
      setCode("");
      toast.success("Zwei-Faktor-Authentifizierung aktiviert.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code ungültig oder abgelaufen.");
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await twoFactorDisable(password);
      setUser(u);
      setStep("idle");
      setPassword("");
      toast.success("Zwei-Faktor-Authentifizierung deaktiviert.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Passwort ist falsch.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-card p-6 space-y-4" data-testid="twofa-settings">
      <div className="flex items-center gap-2">
        {enabled ? (
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
        ) : (
          <ShieldAlert className="h-5 w-5 text-muted-foreground" />
        )}
        <h2 className="font-heading text-lg font-medium">Zwei-Faktor-Authentifizierung</h2>
        <span
          className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-full ${
            enabled ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"
          }`}
          data-testid="twofa-status"
        >
          {enabled ? "Aktiv" : "Inaktiv"}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        Zusätzlicher Schutz: Bei der Anmeldung senden wir Ihnen einen 6-stelligen Code per E-Mail.
      </p>

      {!enabled && step === "idle" && (
        <Button onClick={startEnable} disabled={busy} className="gap-2" data-testid="twofa-enable">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Zwei-Faktor aktivieren
        </Button>
      )}

      {!enabled && step === "confirm" && (
        <form onSubmit={confirmEnable} className="space-y-3" data-testid="twofa-confirm-form">
          <div className="space-y-2">
            <Label htmlFor="tf-code">Bestätigungscode (E-Mail)</Label>
            <Input
              id="tf-code"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="bg-white"
              data-testid="twofa-confirm-code"
              required
            />
          </div>
          <Button type="submit" disabled={busy} className="gap-2" data-testid="twofa-confirm-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Aktivierung bestätigen
          </Button>
        </form>
      )}

      {enabled && step === "idle" && (
        <Button
          variant="secondary"
          className="border border-border gap-2"
          onClick={() => setStep("disable")}
          data-testid="twofa-disable"
        >
          Zwei-Faktor deaktivieren
        </Button>
      )}

      {enabled && step === "disable" && (
        <form onSubmit={confirmDisable} className="space-y-3" data-testid="twofa-disable-form">
          <div className="space-y-2">
            <Label htmlFor="tf-pw">Zur Bestätigung: aktuelles Passwort</Label>
            <Input
              id="tf-pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="bg-white"
              data-testid="twofa-disable-password"
              required
            />
          </div>
          <Button type="submit" variant="destructive" disabled={busy} className="gap-2" data-testid="twofa-disable-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Deaktivieren
          </Button>
        </form>
      )}
    </div>
  );
}
